import { usePlayground } from "@/components/Playground2/playground-store";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import useXRayPromptBlueprint from "@/queries/playground/useXRayBlueprint";
import { StethoscopeIcon } from "lucide-react";
import { reaction, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";

const DEBOUNCE_DELAY = 500;

const XRayButton = observer(() => {
  const playground = usePlayground();
  const [debouncedBlueprint, setDebouncedBlueprint] = useState(() =>
    JSON.parse(JSON.stringify(toJS(playground.blueprint))),
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debounceUpdateBlueprint = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedBlueprint(
        JSON.parse(JSON.stringify(toJS(playground.blueprint))),
      );
    }, DEBOUNCE_DELAY);
  }, [playground.blueprint]);

  useEffect(() => {
    const dispose = reaction(
      () => JSON.stringify(toJS(playground.blueprint)),
      () => {
        debounceUpdateBlueprint();
      },
    );

    return () => {
      dispose();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debounceUpdateBlueprint, playground]);

  useEffect(() => {
    const dispose = reaction(
      () => playground.messagesContent,
      () => {
        debounceUpdateBlueprint();
      },
    );

    return () => {
      dispose();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debounceUpdateBlueprint, playground]);

  const { data, isLoading } = useXRayPromptBlueprint(debouncedBlueprint);

  useEffect(() => {
    if (data?.prompt_blueprint) {
      playground.setXRayBlueprint(data.prompt_blueprint);
    }
  }, [playground, data]);

  if (!data?.expanded) return null;

  return (
    <HeadlessXRayButton
      toggleXRay={playground.toggleXRayMode}
      isXRayMode={playground.isXRayMode}
      isLoading={isLoading}
    />
  );
});

export const HeadlessXRayButton = ({
  toggleXRay,
  isXRayMode,
  isLoading = false,
}: {
  toggleXRay: () => void;
  isXRayMode: boolean;
  isLoading: boolean;
}) => {
  return (
    <BasicTooltip content="Toggle X-Ray Mode">
      <Button
        onClick={toggleXRay}
        variant={isXRayMode ? "blueOutline" : "ghost"}
        size="sm"
        disabled={isLoading}
        className={isXRayMode ? "bg-blue-50" : ""}
      >
        <StethoscopeIcon
          className={`h-4 w-4 transition-all duration-300 ${
            isXRayMode ? "scale-110 text-blue-600" : "text-gray-600"
          }`}
          aria-hidden="true"
        />
      </Button>
    </BasicTooltip>
  );
};

export default XRayButton;
